import { useEffect, useRef } from "react";
import patternSvg from "../../public/pattern.svg?raw";
import { drawSparkSprite } from "../lib/drawSpark.js";
import { parsePattern } from "../lib/patternSvg.js";
import { appearAlpha, glowProfile, patternSparkOffset, SPARK_CORE_TINT } from "../lib/spark.js";
import styles from "./PatternSparksCanvas.module.css";

const PATTERN = parsePattern(patternSvg);

/** Sprite radius as a multiple of the dot's radius in the SVG. */
const SPARK_SCALE = 8;
/** Room around the viewBox for the outer sparks' glow, in SVG units. */
const MARGIN = 40;
/** Halo brightness of the dim sparks, as a share of their usual glow. */
const DIM_GLOW = 0.55;
/** Sprite size of the bright spark, as a multiple of the others'. */
const BRIGHT_HALO = 1.6;
const LINK_WIDTH = 1;
const LINK_ALPHA = 0.8;
/** The show's link colour (uTint in lines.vert.glsl). */
const LINK_COLOR = `rgb(${SPARK_CORE_TINT.r}, ${SPARK_CORE_TINT.g}, ${SPARK_CORE_TINT.b})`;

/** The finished pattern, every dot a spark drifting in place and one of them bright. */
export function PatternSparksCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const brightIndex = Math.floor(Math.random() * PATTERN.points.length);
    let width = 0;
    let height = 0;

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    const start = performance.now();
    let frame = 0;
    const tick = (now: number): void => {
      ctx.clearRect(0, 0, width, height);
      // Contain fit of the viewBox plus margin, centred.
      const boxW = PATTERN.width + 2 * MARGIN;
      const boxH = PATTERN.height + 2 * MARGIN;
      const scale = Math.min(width / boxW, height / boxH);
      const originX = (width - PATTERN.width * scale) / 2;
      const originY = (height - PATTERN.height * scale) / 2;
      const alpha = appearAlpha(now - start);
      const drift = motionQuery.matches ? 0 : undefined;
      const sparks = PATTERN.points.map((point, i) => {
        const radius = point.r * SPARK_SCALE * scale;
        const offset = patternSparkOffset(i, now, drift);
        return {
          x: originX + point.x * scale + offset.dx * radius,
          y: originY + point.y * scale + offset.dy * radius,
          radius,
          phaseMs: offset.phaseMs,
        };
      });

      // Under the sprites, so the lines run into their cores.
      ctx.save();
      ctx.globalAlpha = alpha * LINK_ALPHA;
      ctx.strokeStyle = LINK_COLOR;
      ctx.lineWidth = LINK_WIDTH;
      ctx.beginPath();
      for (const [a, b] of PATTERN.links) {
        ctx.moveTo(sparks[a]!.x, sparks[a]!.y);
        ctx.lineTo(sparks[b]!.x, sparks[b]!.y);
      }
      ctx.stroke();
      ctx.restore();

      sparks.forEach((spark, i) => {
        const bright = i === brightIndex;
        const profile = glowProfile(bright ? 1 : 0, now + spark.phaseMs);
        const haloScale = bright ? BRIGHT_HALO : 1;
        drawSparkSprite(
          ctx,
          spark.x,
          spark.y,
          spark.radius * haloScale,
          {
            ...profile,
            // Spikes stay at full strength, so dim sparks keep the show's yellow-white centre.
            intensity: bright ? profile.intensity : profile.intensity * DIM_GLOW,
          },
          alpha,
        );
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={styles.canvas}
      style={{
        aspectRatio: `${PATTERN.width + 2 * MARGIN} / ${PATTERN.height + 2 * MARGIN}`,
      }}
      aria-hidden="true"
    />
  );
}
